import { Typography } from "@/components";
import { cn } from "@/libraries/cn";
import { CASE_MEMBER_TYPE } from "@/libraries/constant";
import { fromYearToAge } from "@/libraries/date";
import { readableHumanDate } from "@/libraries/dayjs-relative";
import type { Case } from "@/types";
import ArchiveRoundedIcon from "@mui/icons-material/ArchiveRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import Card from "@mui/material/Card";
import type { CardProps } from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import { useNavigate } from "@tanstack/react-router";
import isEqual from "fast-deep-equal";
import { Suspense, lazy, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";

const MarkdownMessage = lazy(
	() => import("@/components/ui/message/item/MarkdownMessage"),
);

interface CaseCardProps extends CardProps {
	caseItem: Case;
	/**
	 * @default true
	 */
	showLastMessage?: boolean;
	/**
	 * @default "case"
	 */
	type?: "case" | "item";
}

const CaseCard = (props: CaseCardProps) => {
	const { t } = useTranslation("common");
	const {
		showLastMessage = true,
		type = "case",
		caseItem,
		className,
		...chatProps
	} = props;

	const navigate = useNavigate();

	const title = useMemo(() => {
		if (!caseItem.patient) return caseItem.title;
		return [caseItem.patient.name, caseItem.patient.surname].join(" ");
	}, [caseItem]);

	const age = useMemo(() => {
		if (!caseItem.patient?.dob)
			return fromYearToAge(caseItem.year_of_birth, " yo");
		return fromYearToAge(caseItem.patient.dob, " yo");
	}, [caseItem]);

	const subtitle = useMemo(() => {
		if (!caseItem.patient) return caseItem.summary;
		return caseItem.title;
	}, [caseItem]);

	const date = useMemo(
		() => readableHumanDate(caseItem.updated_at),
		[caseItem.updated_at],
	);

	return (
		<Card
			variant="outlined"
			className={cn("w-full h-full !rounded-xl !shadow-sm", className)}
			{...chatProps}
		>
			{type === "case" &&
			(caseItem.member_type === CASE_MEMBER_TYPE.EXTERNAL_REQUEST_MEMBER ||
				caseItem.channel_name.startsWith("external_request")) ? (
				<div className="w-full bg-[#DBEAFE] px-4 py-1 flex justify-start items-center gap-2">
					<VisibilityRoundedIcon color="primary" />
					<Typography fontWeight={400} fontSize={12} className="text-zinc-950">
						{t("viewOnly")}
					</Typography>
				</div>
			) : null}
			<div className="space-y-3 px-4 py-2">
				<div className="flex justify-between items-center">
					<div className="space-y-1">
						<Typography fontWeight={700} fontSize={14}>
							{title}
						</Typography>
						<Typography fontWeight={400} fontSize={12}>
							{[age, subtitle].join(" • ")}
						</Typography>
						<Typography fontWeight={400} fontSize={10}>
							{date}
						</Typography>
					</div>
					<div className="flex justify-center items-center gap-1">
						{type === "case" ? (
							<>
								<IconButton
									color="primary"
									onClick={(e) => {
										e.stopPropagation();
										navigate({
											to: "/dashboard/case/view/$id/detail",
											params: { id: caseItem.id },
										});
									}}
								>
									<PersonRoundedIcon />
								</IconButton>
								<IconButton
									color="primary"
									onClick={(e) => {
										e.stopPropagation();
										navigate({
											to: "/dashboard/case/view/$id/detail",
											params: { id: caseItem.id },
											search: { tab: "medical_notes" },
										});
									}}
								>
									<AssignmentRoundedIcon />
								</IconButton>
							</>
						) : (
							<ChevronRightRoundedIcon />
						)}
					</div>
				</div>
				{caseItem.status === "closed" ? (
					<div className="flex-none w-fit bg-[#71717A] rounded-md text-white py-1 px-2 flex justify-center items-center gap-1">
						<ArchiveRoundedIcon fontSize="small" />
						<Typography fontWeight={700} fontSize={12} className="text-white">
							{t("closed")}
						</Typography>
					</div>
				) : null}
				{caseItem.last_message && showLastMessage ? (
					<Suspense>
						<Divider className="w-full" />
						<div className="flex justify-center items-start">
							<div className="flex-1">
								<MarkdownMessage
									variant="cases"
									text={caseItem.last_message.text}
									lineClamp={1}
									isStrike={caseItem.last_message.strike}
									isDeleted={caseItem.last_message.is_deleted}
									documentCount={caseItem.last_message.document_count}
								/>
							</div>
							{caseItem.unread ? (
								<div className="w-6 h-6 flex justify-center items-center bg-secondary rounded-full">
									<Typography fontSize={12}>{caseItem.unread}</Typography>
								</div>
							) : null}
						</div>
					</Suspense>
				) : null}
			</div>
		</Card>
	);
};

export default memo(CaseCard, (prevProps, nextProps) =>
	isEqual(prevProps.caseItem, nextProps.caseItem),
);
